import { getDidService } from "@ntconnect/framework/pbx";

/**
 * Numbers (DIDs) API — wraps the framework DidService, which proxies the
 * ntconnect DID surface through the BFF (/ntconnect/user/voxcraft/voip/trunk/...).
 *
 * The DID list comes back as a HAL collection; `fetchNumbers` normalizes it to
 * the { ok, data: { data, total } } shape the list controller already expects.
 * Getting a number is a two-step reserve → assign; deleting is a release by TN.
 */

export const HAL_PAGE_SIZE = 25; // ntconnect DID list is fixed at 25/page.

// domain is accepted for signature-compatibility with the other list
// controllers; the DID list is per-trunk and has no server-side search.
export const fetchNumbers = async (domain, page = 1) => {
  const res = await getDidService().listDids(page);
  if (!res.ok) return res;
  const dids = res.data?._embedded?.dids || [];
  return {
    ok: true,
    data: { data: dids, total: res.data?.total_items ?? dids.length },
    status: res.status,
  };
};

// Fetch every DID across all HAL pages. The list is per-trunk (typically small)
// and has no server-side search, so the controller pulls the full set once and
// filters/paginates client-side.
export const fetchAllNumbers = async () => {
  const first = await getDidService().listDids(1);
  if (!first.ok) return first;
  let dids = first.data?._embedded?.dids || [];
  const total = first.data?.total_items ?? dids.length;
  const pages = Math.ceil(total / HAL_PAGE_SIZE);
  for (let page = 2; page <= pages; page++) {
    const res = await getDidService().listDids(page);
    if (!res.ok) break;
    dids = dids.concat(res.data?._embedded?.dids || []);
  }
  return { ok: true, data: { data: dids, total: dids.length }, status: first.status };
};

export const fetchNumber = (idOrTn) => getDidService().getDid(idOrTn);

export const fetchPrimaryNumber = () => getDidService().getPrimaryDid();

// ---- Search available numbers ----
export const searchNpa = (country, region) =>
  getDidService().searchNpa(country, region);

export const searchNpaNxx = (country, region, filter) =>
  getDidService().searchNpaNxx(country, region, filter);

// ---- Acquire: reserve then assign ----
export const reserveNumber = (input) => getDidService().reserveDid(input);

// tn is coerced to a string — the reserve response can return it as a number,
// but the assign endpoint validates tn as a string (422 otherwise).
export const assignNumber = (tn) => getDidService().assignDid(String(tn));

// ---- Release ----
export const releaseNumber = (tn) => getDidService().releaseDid(String(tn));
